import React, { forwardRef } from "react";

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
  draggable?: boolean;
  disabled?: boolean;
  deselecting?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { className, indeterminate, draggable, deselecting, disabled, ...props },
    ref
  ) => {
    const checkboxRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate ?? false;
      }
    }, [indeterminate]);

    const inputClassName = `peer h-4 w-4 shrink-0 appearance-none rounded ring ring-slate-600 
             checked:bg-indigo-300 
            indeterminate:border-indigo-600 indeterminate:bg-indigo-600 
            focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 
            disabled:border-slate-300 disabled:bg-slate-700
            dark:border-slate-500 dark:bg-slate-800 dark:ring-slate-500
            dark:checked:border-indigo-400 dark:checked:bg-indigo-600 dark:checked:ring-indigo-400
            dark:checked:hover:bg-indigo-500 dark:checked:hover:ring-indigo-300
            dark:indeterminate:border-indigo-500 dark:indeterminate:bg-indigo-500
            group-hover/row:bg-slate-900
            ${draggable ? "hover:ring-2 hover:ring-indigo-400" : ""}
            ${deselecting ? "" : ""}
            ${disabled ? "!opacity-50 !cursor-not-allowed" : ""}
            forced-colors:appearance-auto ${className || ""}`;

    const iconWrap =
      "pointer-events-none absolute inset-0 flex items-center justify-center text-white transition-opacity peer-disabled:text-slate-300/40 dark:peer-disabled:text-slate-500/50";

    return (
      <div
        className={`relative flex size-4 shrink-0 items-center justify-center ${
          draggable ? "cursor-grab active:cursor-grabbing" : ""
        }`}
      >
        <input
          type="checkbox"
          disabled={disabled}
          ref={(el) => {
            if (typeof ref === "function") {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
            checkboxRef.current = el;
          }}
          className={inputClassName}
          {...props}
        />
        {/* Checkmark: only when checked; hidden when indeterminate or unchecked (peer + sibling is the reliable pattern). */}
        <span
          aria-hidden
          className={`${iconWrap} opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0`}
        >
          <svg fill="none" viewBox="0 0 14 14" className="size-3.5">
            <path
              d="M3 8L6 11L11 3.5"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {/* Indeterminate dash */}
        <span
          aria-hidden
          className={`${iconWrap} opacity-0 peer-checked:opacity-0 peer-indeterminate:opacity-100`}
        >
          <svg fill="none" viewBox="0 0 14 14" className="size-3.5">
            <path
              d="M3 7H11"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox };
